// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineDensity } from '@/constants/timeline-density';
import { useTimelineZoom } from '@/hooks/use-timeline-zoom';
import { getTimelineInterval } from '@/utilities/get-timeline-interval';
import { DateTime } from 'luxon';
import { useState } from 'react';

const UNIT_WIDTH = 100;
const DEFAULT_DATE_TIME = DateTime.now().startOf('day');

/**
 * A React hook that manages the state of a timeline.
 *
 * @param width - The width of the timeline.
 * @param defaultZoom - The default zoom level of the timeline.
 * @param initialDateTime - The initial date and time of the timeline.
 */
export function useTimelineState(
  width: number,
  defaultZoom: TimelineDensity,
  initialDateTime: DateTime = DEFAULT_DATE_TIME
) {
  const [dateTime, setDateTime] = useState(initialDateTime);
  const timelineZoom = useTimelineZoom(defaultZoom);

  const interval = getTimelineInterval(
    dateTime,
    timelineZoom.zoom,
    width,
    UNIT_WIDTH
  );

  const shiftForward = (count: number) => {
    setDateTime((dateTime) => dateTime.plus({ [timelineZoom.zoom]: count }));
  };

  const shiftBackward = (count: number) => {
    setDateTime((dateTime) => dateTime.minus({ [timelineZoom.zoom]: count }));
  };

  return {
    ...timelineZoom,
    interval,
    setDateTime,
    shiftForward,
    shiftBackward,
  };
}
